export const ORG_ROLES = ["owner", "admin", "editor", "viewer"] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

export const ROLE_LABELS: Record<OrgRole, string> = {
  owner: "Owner",
  admin: "Admin",
  editor: "Editor",
  viewer: "Betrachter",
};

const ROLE_RANK: Record<OrgRole, number> = { viewer: 0, editor: 1, admin: 2, owner: 3 };

/**
 * Minimum role required per action. Enforced in the backend for every request
 * (web app and MCP alike) and used by the frontend to hide unavailable actions.
 */
export const PERMISSIONS = {
  "org.read": "viewer",
  "org.update": "admin",
  "org.delete": "owner",
  "members.manage": "admin",
  "project.create": "editor",
  "project.update": "editor",
  "project.delete": "admin",
  "document.create": "editor",
  "document.update": "editor",
  "document.delete": "editor",
  "document.export": "editor",
  "media.upload": "editor",
  "media.update": "editor",
  "media.delete": "editor",
  "trash.restore": "editor",
  "trash.purge": "admin",
  "storage.read": "viewer",
  "brand.update": "admin",
  /** Comments: everyone who can see a file (also viewers, e.g. a boss). */
  "comment.create": "viewer",
  /** Share links that open a file without an account. */
  "share.manage": "editor",
  /** Book, change and cancel the subscription (docs/16-geschaeftsmodell.md). */
  "billing.manage": "owner",
} as const satisfies Record<string, OrgRole>;

export type Permission = keyof typeof PERMISSIONS;

/**
 * What still works while an organization is read-only because it has no
 * running subscription: looking at everything, running the organization and
 * booking a plan. No editing, uploading, background jobs or exports.
 */
export const READ_ONLY_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>([
  "org.read",
  "org.update",
  "org.delete",
  "members.manage",
  "storage.read",
  "billing.manage",
]);

export function isOrgRole(value: unknown): value is OrgRole {
  return typeof value === "string" && (ORG_ROLES as readonly string[]).includes(value);
}

/** Better Auth stores multiple roles comma-separated; the highest one wins. */
export function effectiveRole(raw: string | null | undefined): OrgRole | null {
  if (!raw) return null;
  let best: OrgRole | null = null;
  for (const part of raw.split(",")) {
    const role = part.trim();
    if (isOrgRole(role) && (best === null || ROLE_RANK[role] > ROLE_RANK[best])) best = role;
  }
  return best;
}

export function hasRole(role: OrgRole | null | undefined, minimum: OrgRole): boolean {
  return role != null && ROLE_RANK[role] >= ROLE_RANK[minimum];
}

export function can(
  role: OrgRole | null | undefined,
  permission: Permission,
  readOnly = false,
): boolean {
  if (readOnly && !READ_ONLY_PERMISSIONS.has(permission)) return false;
  return hasRole(role, PERMISSIONS[permission]);
}
